import {
    ApartmentOutlined,
    CheckSquareOutlined,
    LineChartOutlined,
    MedicineBoxOutlined,
    PictureOutlined,
    SoundOutlined,
    TeamOutlined
} from "@ant-design/icons";
import { ReactNode } from "react";
import { RouteNames } from "./routes";

export interface NavItem {
    /** Route segment; doubles as the item's key. */
    route: string;
    /** i18n key of the item's label. */
    labelKey: string;
    icon: ReactNode;
}

export interface NavGroup {
    /** i18n key of the group's eyebrow label. */
    labelKey: string;
    items: NavItem[];
}

/**
 * The single description of the primary navigation.
 *
 * The sidebar renders it, the topbar reads it to name the current location, and
 * the command palette searches it — so a new section is added in one place.
 *
 * Account is deliberately absent: signing out and looking at the current
 * identity belong to the user menu in the topbar, not to a section of the app.
 * The route still exists and the palette still finds it.
 */
export const navGroups: NavGroup[] = [
    {
        labelKey: "nav.group.directory",
        items: [
            {
                route: RouteNames.organizations,
                labelKey: "menu.organizations",
                icon: <ApartmentOutlined />
            },
            { route: RouteNames.individuals, labelKey: "menu.individuals", icon: <TeamOutlined /> }
        ]
    },
    {
        labelKey: "nav.group.careLibrary",
        items: [
            {
                route: RouteNames.measurementTypes,
                labelKey: "menu.measurementTypes",
                icon: <LineChartOutlined />
            },
            {
                route: RouteNames.activities,
                labelKey: "menu.activities",
                icon: <CheckSquareOutlined />
            },
            {
                route: RouteNames.treatments,
                labelKey: "menu.treatments",
                icon: <MedicineBoxOutlined />
            }
        ]
    },
    {
        labelKey: "nav.group.media",
        items: [
            {
                route: RouteNames.imagesLibrary,
                labelKey: "menu.imagesLibrary",
                icon: <PictureOutlined />
            },
            { route: RouteNames.audioLibrary, labelKey: "menu.audioLibrary", icon: <SoundOutlined /> }
        ]
    }
];

export const navItems: NavItem[] = navGroups.flatMap(group => group.items);

/** The nav entry a pathname belongs to, matched on its first segment. */
export function findNavItem(pathname: string): NavItem | undefined {
    const segment = pathname.split("/").filter(Boolean)[0];
    return navItems.find(item => item.route === segment);
}

/** The group a nav entry sits in, for the topbar trail. */
export function findNavGroup(item: NavItem | undefined): NavGroup | undefined {
    if (item == null) return undefined;
    return navGroups.find(group => group.items.includes(item));
}
